import Button from "@/components/button";
import CheckIcon from "@/components/checkIcon";
import { PricingTier } from "@/redux/slices/plan";
import { PeriodicityTranslate } from "@/service/plans";
import { cn, numberToMoney } from "@/utils";

interface PlanCardProps {
  plan: PricingTier;
  onSubscribe: (plan: PricingTier) => void;
  onShowCompanies: (plan: PricingTier) => void;
  className?: string;
}

function PlanMeta({
  plan,
  onShowCompanies,
}: {
  plan: PricingTier;
  onShowCompanies: (plan: PricingTier) => void;
}) {
  if (plan.allCompanies) {
    return (
      <p className="text-xs text-checkout-muted">Aceito em todas as unidades</p>
    );
  }

  return (
    <button
      type="button"
      className="text-xs text-[var(--tenant-accent-color)] underline-offset-4 hover:underline"
      onClick={() => onShowCompanies(plan)}
    >
      Aceito em algumas unidades
    </button>
  );
}

export function PlanHeroCard({
  plan,
  onSubscribe,
  onShowCompanies,
  className,
}: PlanCardProps) {
  return (
    <article
      className={cn(
        "relative overflow-hidden rounded-shell border border-checkout-hairline bg-checkout-surface p-1.5",
        className ?? "md:col-span-7 md:row-span-2"
      )}
    >
      <div className="relative flex h-full min-h-[340px] flex-col rounded-[calc(1.75rem-0.375rem)] bg-checkout-raised p-8">
        <span className="w-fit rounded-full border border-checkout-hairline px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-checkout-muted">
          Mais assinado
        </span>
        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight md:text-5xl">
          {plan.name}
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-6 text-checkout-muted md:text-base">
          {plan.description}
        </p>
        <ul className="mt-8 space-y-3 text-sm">
          {plan.services.map((service) => (
            <li
              key={service.IdSubscriptionService}
              className="flex items-center gap-3"
            >
              <CheckIcon />
              {service.Service.Description}
            </li>
          ))}
        </ul>
        <div className="mt-auto flex flex-col gap-4 pt-10 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <PlanMeta plan={plan} onShowCompanies={onShowCompanies} />
            <p className="mt-2 font-display text-3xl font-semibold">
              {numberToMoney(plan.price)}
              <span className="ml-1 text-base font-medium text-checkout-muted">
                /{PeriodicityTranslate[plan.periodicity]}
              </span>
            </p>
          </div>
          <Button onClick={() => onSubscribe(plan)} className="sm:min-w-48">
            Assinar este plano
          </Button>
        </div>
      </div>
    </article>
  );
}

export function PlanCompactCard({
  plan,
  onSubscribe,
  onShowCompanies,
}: PlanCardProps) {
  return (
    <article className="rounded-shell border border-checkout-hairline bg-checkout-surface p-1.5">
      <div className="flex h-full flex-col rounded-[calc(1.75rem-0.375rem)] bg-checkout-raised p-6">
        <h3 className="font-display text-xl font-semibold">{plan.name}</h3>
        <p className="mt-3 line-clamp-3 flex-1 text-sm text-checkout-muted">
          {plan.description}
        </p>
        <ul className="mt-5 space-y-2 text-sm">
          {plan.services.slice(0, 3).map((service) => (
            <li
              key={service.IdSubscriptionService}
              className="flex items-center gap-2"
            >
              <CheckIcon className="h-4 w-4" />
              {service.Service.Description}
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <PlanMeta plan={plan} onShowCompanies={onShowCompanies} />
          <p className="mt-1 font-display text-2xl font-semibold">
            {numberToMoney(plan.price)}
            <span className="ml-1 text-sm font-medium text-checkout-muted">
              /{PeriodicityTranslate[plan.periodicity]}
            </span>
          </p>
        </div>
        <Button
          className="mt-5 w-full"
          onClick={() => onSubscribe(plan)}
        >
          Assinar este plano
        </Button>
      </div>
    </article>
  );
}

export function PlanRow({
  plan,
  onSubscribe,
  onShowCompanies,
}: PlanCardProps) {
  return (
    <article className="rounded-shell border border-checkout-hairline bg-checkout-surface p-1.5">
      <div
        className={cn(
          "grid gap-6 rounded-[calc(1.75rem-0.375rem)] bg-checkout-raised p-6 md:grid-cols-[1.2fr_1fr_auto] md:items-center"
        )}
      >
        <div>
          <h3 className="font-display text-xl font-semibold">{plan.name}</h3>
          <p className="mt-2 text-sm text-checkout-muted">{plan.description}</p>
          <div className="mt-3">
            <PlanMeta plan={plan} onShowCompanies={onShowCompanies} />
          </div>
        </div>
        <ul className="space-y-2 text-sm">
          {plan.services.map((service) => (
            <li
              key={service.IdSubscriptionService}
              className="flex items-center gap-2"
            >
              <CheckIcon className="h-4 w-4" />
              {service.Service.Description}
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-stretch gap-3 md:min-w-48 md:items-end">
          <p className="font-display text-2xl font-semibold">
            {numberToMoney(plan.price)}
            <span className="ml-1 text-sm font-medium text-checkout-muted">
              /{PeriodicityTranslate[plan.periodicity]}
            </span>
          </p>
          <Button onClick={() => onSubscribe(plan)}>Assinar este plano</Button>
        </div>
      </div>
    </article>
  );
}
